import React, { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { Loader2 } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, PieChart, Pie, LineChart, Line } from "recharts";

export default function Statistiques() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <div className="flex h-96 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;

  const moisData = s.par_mois.map((m) => ({ mois: m.mois.slice(5), count: m.count }));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-slate-900">Statistiques</h1>
        <p className="text-sm text-slate-500">Vue d'ensemble de l'activité</p>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat label="Total visites" value={s.total} color="#3b82f6" />
        <Stat label="Taux de réalisation" value={`${s.taux_realisation} %`} color="#10b981" />
        <Stat label="Taux d'annulation" value={`${s.taux_annulation} %`} color="#ef4444" />
        <Stat label="Techniciens actifs" value={s.par_technicien.length} color="#14b8a6" />
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card title="Visites par statut">
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={s.par_statut.filter((x) => x.count > 0)} dataKey="count" nameKey="statut" cx="50%" cy="50%" outerRadius={100} label={(e) => e.count}>
                {s.par_statut.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Visites par technicien">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={s.par_technicien} layout="vertical" margin={{ left: 40 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="nom" width={110} tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} barSize={14} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Visites par installateur">
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={s.par_installateur} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="nom" tick={{ fontSize: 10, fill: "#94a3b8" }} axisLine={false} tickLine={false} interval={0} angle={-15} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} cursor={{ fill: "#f8fafc" }} />
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]} barSize={30} />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Visites par mois (6 derniers mois)">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={moisData} margin={{ left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="mois" tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
              <Line type="monotone" dataKey="count" stroke="#10b981" strokeWidth={2.5} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="stats-partenaires">
        <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-slate-700">Détail par partenaire</h3>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wide text-slate-500">
              <th className="px-4 py-2.5">Partenaire</th><th className="px-4 py-2.5">Total</th><th className="px-4 py-2.5">En cours</th><th className="px-4 py-2.5">Terminées</th><th className="px-4 py-2.5">Annulées</th><th className="px-4 py-2.5">Taux réalisation</th><th className="px-4 py-2.5">Mode</th>
            </tr></thead>
            <tbody>
              {s.par_installateur.length === 0 ? (
                <tr><td colSpan={7} className="py-10 text-center text-slate-400">Aucune donnée</td></tr>
              ) : s.par_installateur.map((p) => (
                <tr key={p.nom} className="border-b border-slate-100 hover:bg-slate-50/60">
                  <td className="px-4 py-2.5 font-medium text-slate-900">{p.nom}</td>
                  <td className="px-4 py-2.5 mono">{p.count}</td>
                  <td className="px-4 py-2.5 mono text-orange-500">{p.en_cours}</td>
                  <td className="px-4 py-2.5 mono text-emerald-600">{p.terminees}</td>
                  <td className="px-4 py-2.5 mono text-rose-500">{p.annulees}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="h-1.5 w-20 overflow-hidden rounded-full bg-slate-100"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${p.taux_realisation}%` }} /></div>
                      <span className="mono text-xs text-slate-600">{p.taux_realisation}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold ${p.mode === "prepaiement" ? "bg-violet-100 text-violet-700" : "bg-slate-100 text-slate-600"}`}>{p.mode === "prepaiement" ? "Prépaiement" : "Classique"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
function Stat({ label, value, color }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-1 font-heading text-3xl font-black" style={{ color }}>{value}</div>
    </div>
  );
}
function Card({ title, children }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5">
      <h3 className="mb-4 font-heading text-sm font-bold uppercase tracking-wide text-slate-700">{title}</h3>
      {children}
    </div>
  );
}
